import React, { useState, useEffect, useCallback } from 'react';
import { axios, useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

// Mobil uyumluluk için yardımcı stil objesi
const mobileStyles = `
  @media (max-width: 768px) {
    .responsive-grid-form { grid-template-columns: 1fr !important; }
    .responsive-grid-stats { grid-template-columns: 1fr 1fr 1fr !important; gap: 8px !important; }
    .stat-card { padding: 10px !important; }
    .stat-card div { font-size: 1.2rem !important; }
    
    .section-header { flex-direction: column; align-items: flex-start !important; gap: 10px; }
    
    /* Geçmiş Yoklamalar Tablosu Mobil Görünüm */
    .table-container table, .table-container thead, .table-container tbody, .table-container th, .table-container td, .table-container tr { 
      display: block; 
    }
    .table-container thead tr { position: absolute; top: -9999px; left: -9999px; }
    .table-container tr { border: 1px solid var(--border); margin-bottom: 12px; border-radius: 12px; padding: 10px; background: var(--bg-card); }
    .table-container td { border: none; position: relative; padding-left: 45% !important; text-align: right !important; min-height: 34px; display: flex; align-items: center; justify-content: flex-end; }
    .table-container td:before { 
      content: attr(data-label); position: absolute; left: 10px; width: 40%; padding-right: 10px; 
      white-space: nowrap; text-align: left; font-weight: 700; color: var(--text-secondary); font-size: 0.8rem;
    }
    .mobile-full-btn { width: 100%; margin-top: 8px; }

    /* Yoklama Listesi Elemanları */
    .yoklama-item { padding: 10px 8px !important; gap: 8px !important; }
    .yoklama-actions { gap: 4px !important; }
    .yoklama-actions button { width: 32px !important; height: 32px !important; font-size: 1rem !important; }
  }
`;

export default function YoklamaPage() {
  const { user } = useAuth();
  const canManageAllClasses = ['rehber_ogretmen', 'ogrenci_isleri', 'kurucu'].includes(user?.rol);
  const [siniflar, setSiniflar] = useState([]);
  const [dersler, setDersler] = useState([]);
  const [aktifYoklama, setAktifYoklama] = useState(null);
  const [liste, setListe] = useState([]);
  const [loading, setLoading] = useState(false);
  const [gecmis, setGecmis] = useState([]);
  const [isReadOnly, setIsReadOnly] = useState(false); 
  
  const [form, setForm] = useState({ 
    sinif_id: '', 
    ders_id: '', 
    tarih: new Date().toISOString().slice(0,10),
    baslangic_saati: new Date().toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }) 
  });

  const loadGecmisYoklamalar = useCallback(async () => {
    if (!canManageAllClasses) {
      return axios.get('/yoklama/ogretmen/gecmis').catch(() => ({ data: { success: false, data: [] } }));
    }

    try {
      return await axios.get('/yoklama/yonetim/gecmis');
    } catch {
      return axios.get('/yoklama/rehber/gecmis').catch(() => ({ data: { success: false, data: [] } }));
    }
  }, [canManageAllClasses]);

  const loadSiniflar = useCallback(async () => {
    try {
      const [sinifRes, dersRes, ogRes] = await Promise.all([
        axios.get('/siniflar'), 
        axios.get('/dersler'),  
        loadGecmisYoklamalar(),
      ]);
      
      if (sinifRes.data.success) setSiniflar(sinifRes.data.data);
      if (dersRes.data.success) setDersler(dersRes.data.data);
      if (ogRes.data.success) setGecmis((ogRes.data.data || []).slice(0, 20));
    } catch (err) {
      console.error("Yükleme hatası:", err);
    }
  }, [loadGecmisYoklamalar]);

  useEffect(() => { loadSiniflar(); }, [loadSiniflar]);

  const handleDuzenle = async (yoklama) => {
    const dbTarih = new Date(yoklama.tarih).toLocaleDateString('en-CA');
    const bugun = new Date().toLocaleDateString('en-CA');
    const readOnlyMode = dbTarih !== bugun;

    try {
    const { data } = await axios.get(`/yoklama/${yoklama.id}`);
    if (data.success) {
      setAktifYoklama(yoklama.id);
      setListe(data.data.kayitlar);
      setIsReadOnly(false); // Her zaman false yaparak engeli kaldırıyoruz
      toast.success("Düzenleme modu aktif.");
    }
  } catch {
    toast.error("Kayıtlar yüklenemedi.");
  }
};

  const handleBaslat = async () => {
    if (!form.sinif_id || !form.ders_id) { toast.error('Sınıf ve ders seçin'); return; }
    setLoading(true);
    setIsReadOnly(false);
    try {
      const { data } = await axios.post('/yoklama/ac', form);
      if (data.success) {
        const yoklamaId = data.data?.id;
        setAktifYoklama(yoklamaId);
        const res = await axios.get(`/yoklama/${yoklamaId}`);
        if (res.data.success) setListe(res.data.data.kayitlar);
        toast.success(`Yoklama başlatıldı.`);
      }
    } catch (e) { 
      toast.error(e.response?.data?.message || 'Hata oluştu'); 
    } finally { setLoading(false); }
  };

  const updateDurumDirectly = async (ogrenciId, yeniDurum) => {
    if (isReadOnly) return; 
    try {
      await axios.put(`/yoklama/${aktifYoklama}/kayit/${ogrenciId}`, { durum: yeniDurum });
      setListe(prev => prev.map(o => {
        const targetId = o.ogrenci_id || o.id;
        return targetId === ogrenciId ? { ...o, durum: yeniDurum } : o;
      }));
    } catch (e) { 
        toast.error("Değişim engellendi (Gün sonu geçmiş olabilir)."); 
    }
  };

 const handleKapat = async () => {
    if (isReadOnly) { 
      setAktifYoklama(null); 
      return; 
    } 

    const onay = window.confirm(
      'Yoklamayı bitirmek istiyor musunuz? Tüm öğrencilere ve velilere durum bildirimi gönderilecektir.'
    );
    if (!onay) return;

    setLoading(true);
    try {
      const { data } = await axios.put(`/yoklama/${aktifYoklama}/kapat`);
      if (data.success) {
        toast.success(data.message || 'Yoklama ve bildirimler tamamlandı!');
        setAktifYoklama(null);
        setListe([]);
        loadSiniflar();
      }
    } catch (err) { 
      toast.error(err.response?.data?.message || 'Kapatma hatası'); 
    } finally {
      setLoading(false);
    }
  };

  const handleIptal = () => {
    if (isReadOnly) { setAktifYoklama(null); return; }
    if (window.confirm('Değişiklikleri kaydetmeden çıkmak istiyor musunuz?')) {
      setAktifYoklama(null);
      setListe([]);
      loadSiniflar();
    }
  };

  const DURUM = {
    mevcut:    { label: 'Var',     bg: '#D1FAE5', color: '#065F46', emoji: '✅', border: '#10B981' },
    devamsiz:  { label: 'Yok',     bg: '#FEE2E2', color: '#991B1B', emoji: '❌', border: '#EF4444' },
    gec:       { label: 'Geç',     bg: '#FEF3C7', color: '#92400E', emoji: '⏰', border: '#F59E0B' }, 
  };

  const mevcut   = liste.filter(o => o.durum === 'mevcut').length;
  const devamsiz = liste.filter(o => o.durum === 'devamsiz').length;
  const gec      = liste.filter(o => o.durum === 'gec').length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24, paddingBottom: 40 }}>
      <style>{mobileStyles}</style>
      <div className="animate-fadeInUp">
        <h2 style={{ fontFamily: 'var(--font-ui)', fontWeight: 900, fontSize: '1.4rem' }}>✅ Yoklama</h2>
        {canManageAllClasses && (
          <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: 4 }}>
            Tüm sınıflar için seçilen tarih ve saatte yoklama alıp geçmiş kayıtları düzenleyin.
          </p>
        )}
      </div>

      {!aktifYoklama ? (
        <>
          <div className="card animate-fadeInUp stagger-1">
            <div style={{ fontFamily: 'var(--font-ui)', fontWeight: 800, fontSize: '1rem', marginBottom: 18, color: 'var(--text-primary)' }}>
              📋 {canManageAllClasses ? 'Sınıf Yoklaması Al' : 'Yeni Yoklama Başlat'}
            </div>
            <div className="responsive-grid-form" style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.2fr 1fr 0.8fr auto', gap: 12, alignItems: 'end' }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Sınıf *</label>
                <select className="form-input" value={form.sinif_id} onChange={e => setForm({...form, sinif_id: e.target.value})}>
                  <option value="">{canManageAllClasses ? 'Sınıf seçin' : 'Sınıf seçin'}</option>
                  {siniflar.map(s => <option key={s.id} value={s.id}>{s.ad}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Ders *</label>
                <select className="form-input" value={form.ders_id} onChange={e => setForm({...form, ders_id: e.target.value})}>
                  <option value="">Ders seçin</option>
                  {dersler.map(d => <option key={d.id} value={d.id}>{d.ad}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Tarih</label>
                <input className="form-input" type="date" value={form.tarih} onChange={e => setForm({...form, tarih: e.target.value})} />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Saat</label>
                <input className="form-input" type="time" value={form.baslangic_saati} onChange={e => setForm({...form, baslangic_saati: e.target.value})} />
              </div>
              <button className="btn btn-primary mobile-full-btn" style={{ height: 42, paddingInline: 20 }} onClick={handleBaslat} disabled={loading}>
                ▶ {canManageAllClasses ? 'Yoklama Al' : 'Başlat'}
              </button>
            </div>
          </div>

          {gecmis.length > 0 && (
            <div className="card animate-fadeInUp stagger-2" style={{ padding: 0 }}>
              <div style={{ fontFamily: 'var(--font-ui)', fontWeight: 800, padding: '16px 16px 0 16px' }}>
                📅 {canManageAllClasses ? 'Son Sınıf Yoklamaları' : 'Son Yoklamalar'}
              </div>
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Tarih</th><th>Sınıf</th><th>Ders</th>
                      <th>Var</th><th>Yok</th><th>Geç</th>
                      <th>İşlem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gecmis.map((y, i) => (
                      <tr key={i}>
                        <td data-label="Tarih" style={{ fontWeight:600 }}>{new Date(y.tarih).toLocaleDateString('tr-TR')}</td>
                        <td data-label="Sınıf">{y.sinif_adi}</td>
                        <td data-label="Ders"><span className="badge" style={{ background:'var(--blue-dim)', color:'var(--blue)' }}>{y.ders_adi}</span></td>
                        <td data-label="Var" style={{ fontWeight:700, color:'#10B981' }}>{y.mevcut_sayisi || 0}</td>
                        <td data-label="Yok" style={{ fontWeight:700, color:'#EF4444' }}>{y.devamsiz_sayisi || 0}</td>
                        <td data-label="Geç" style={{ fontWeight:700, color:'#F59E0B' }}>{y.gec_sayisi || 0}</td>
                        <td data-label="İşlem">
                          <button className="btn btn-sm btn-outline mobile-full-btn" onClick={() => handleDuzenle(y)}>
                            {new Date(y.tarih).toLocaleDateString('en-CA') === new Date().toLocaleDateString('en-CA') ? '✏️ Düzenle' : '👁 Gör'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="animate-fadeInUp">
          <div className="responsive-grid-stats" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
            {[
              { label: 'Var', count: mevcut, color: '#10B981', bg: '#D1FAE5', emoji: '✅' },
              { label: 'Yok', count: devamsiz, color: '#EF4444', bg: '#FEE2E2', emoji: '❌' },
              { label: 'Geç', count: gec, color: '#F59E0B', bg: '#FEF3C7', emoji: '⏰' },
            ].map(stat => (
              <div key={stat.label} className="card stat-card" style={{ background: stat.bg, border: `1.5px solid ${stat.color}30`, textAlign:'center', padding:16 }}>
                <div style={{ fontSize:'1.5rem' }}>{stat.emoji}</div>
                <div style={{ fontFamily:'var(--font-ui)', fontWeight:900, fontSize:'1.6rem', color:stat.color, lineHeight:1.2 }}>{stat.count}</div>
                <div style={{ fontSize:'0.7rem', color:stat.color, fontWeight:700, textTransform: 'uppercase' }}>{stat.label}</div>
              </div>
            ))}
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <div className="section-header" style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
              <div style={{ fontFamily:'var(--font-ui)', fontWeight:800, fontSize:'1rem' }}>
                👥 Liste — {liste.length} Öğrenci {isReadOnly && "(Salt Okunur)"}
              </div>
              <button 
                className="btn btn-sm" 
                style={{ background: isReadOnly ? '#e2e8f0' : '#fee2e2', color: isReadOnly ? '#475569' : '#991b1b' }} 
                onClick={handleIptal}
              >
                {isReadOnly ? '⬅ Geri Dön' : '✖ İptal'}
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection:'column', gap: 8 }}>
              {liste.map((ogr) => {
                const oId = ogr.ogrenci_id || ogr.id;
                const d = DURUM[ogr.durum] || DURUM.devamsiz;
                return (
                  <div key={oId} className="yoklama-item" style={{
                    display:'flex', alignItems:'center', gap:12, padding:'12px 14px',
                    background: d.bg, border: `1.5px solid ${d.border}30`, borderRadius: 'var(--r-md)',
                    opacity: isReadOnly ? 0.85 : 1,
                  }}>
                    <div className="avatar avatar-sm" style={{ background: d.border+'25', color: d.color, minWidth:36 }}>
                      {ogr.ad?.[0]}{ogr.soyad?.[0]}
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontWeight:700, fontSize:'0.85rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {ogr.ad} {ogr.soyad}
                      </div>
                    </div>
                    {!isReadOnly && (
                      <div className="yoklama-actions" style={{ display:'flex', gap:6 }}>
                        {Object.entries(DURUM).map(([key, val]) => (
                          <button 
                            key={key} 
                            onClick={() => updateDurumDirectly(oId, key)} 
                            style={{
                              width:36, height:36, borderRadius:10, border:'none', cursor:'pointer',
                              background: ogr.durum === key ? d.border+'40' : 'rgba(0,0,0,0.05)',
                              outline: ogr.durum === key ? `2px solid ${d.border}` : 'none',
                              fontSize: '1.1rem',
                              transition: 'all 0.2s',
                              display: 'flex', alignItems: 'center', justifyContent: 'center'
                            }}
                          >
                            {val.emoji}
                          </button>
                        ))}
                      </div>
                    )}
                    {isReadOnly && (
                      <div style={{ padding:'4px 10px', borderRadius:99, fontSize:'0.7rem', fontWeight:700, background: d.border+'25', color: d.color }}>
                        {d.label}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          {!isReadOnly && (
            <button className="btn btn-primary btn-lg" style={{ width:'100%', background: 'var(--blue)', fontWeight: 800 }} onClick={handleKapat} disabled={loading}>
              {loading ? 'İşleniyor...' : '✓ Değişiklikleri Kaydet & Bitir'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
